import i18n from '@/plugins/i18n'
import { WorkdaysType } from '@/types/labor'

const now = new Date()
const yyyy = now.getFullYear()
const MM = ('0' + (now.getMonth() + 1)).slice(-2)
const dd = ('0' + now.getDate()).slice(-2)
const hh = ('0' + now.getHours()).slice(-2)
const mm = ('0' + now.getMinutes()).slice(-2)
const ss = ('0' + now.getSeconds()).slice(-2)

/**
 * nowString 2022-02-22 22:22:22
 */
export const nowString =
    yyyy + '-' + MM + '-' + dd + ' ' + hh + ':' + mm + ':' + ss

/**
 * todayString 2022-02-22
 */
export const todayString = yyyy + '-' + MM + '-' + dd

/**
 * new Date()に入れる文字列のsafari対応
 * @param {string} input dateString may has - or T
 * @returns replace - to /, T to ' '
 */
export function dateString(input: string) {
    if (!input) return ''
    return input.toString().replace(/-/g, '/').replace(/T/g, ' ')
}

/**
 * new Date()に入れる文字列のsafari対応(dateStringのタイムゾーン残し)
 * @param {string} input dateString may has - or T
 * @returns replace - to /
 */
export function dateStringTZ(input: string) {
    if (!input) return ''
    return input.toString().replace(/-/g, '/')
}

/**
 * 日本語曜日取得
 * @param {string} date "YYYY-MM-DD HH:MM:SS"
 * @param {string} locale "ja", "en"
 * @returns 曜日
 */
export function getWeekDay(date: string, locale = '') {
    let weekdays = ['日', '月', '火', '水', '木', '金', '土']
    if (locale === 'en')
        weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const index = new Date(dateString(date)).getDay()
    const text = weekdays[index]
    const cssClass = index === 0 ? 'sun' : index === 6 ? 'sat' : 'default'
    return { text: text, cssClass: cssClass }
}

/**
 * ----------------------------- 年月日+時分秒 -----------------------------
 */

/**
 * datetime-local用日時
 * @param {Date} input 'YYYY-MM-DDTHH:MM:SS.000000Z'
 * @returns 'YYYY-MM-DDTHH:MM' for type="datetime-local"
 */
export function datetimeLocal(input: Date) {
    const date = input ? input : new Date()
    const year = date.getFullYear()
    const month = ('00' + (date.getMonth() + 1)).slice(-2)
    const day = ('00' + date.getDate()).slice(-2)
    const hour = ('00' + date.getHours()).slice(-2)
    const minute = ('00' + date.getMinutes()).slice(-2)
    return `${year}-${month}-${day}T${hour}:${minute}`
}

/**
 * type="datetime-local"からAPI用の文字列に直す
 * @param {string} input 'YYYY-MM-DDTHH:MM'
 * @returns 'YYYY/MM/DD HH:MM:SS'
 */
export function datetimeLocalReverse(input: string) {
    const inputReplace = dateString(input)
    return inputReplace.slice(0, 10) + ' ' + inputReplace.slice(11, 16) + ':00'
}

/**
 * dateオブジェクトをAPI用にフォーマット
 * @param {Date} input
 * @returns "YYYY-MM-DD HH:MM:SS"
 */
export function date2YMDHMS(input: Date) {
    const yyyy = input.getFullYear()
    const MM = ('0' + (input.getMonth() + 1)).slice(-2)
    const dd = ('0' + input.getDate()).slice(-2)
    const hh = ('0' + input.getHours()).slice(-2)
    const mm = ('0' + input.getMinutes()).slice(-2)
    const ss = ('0' + input.getSeconds()).slice(-2)
    return yyyy + '-' + MM + '-' + dd + ' ' + hh + ':' + mm + ':' + ss
}

/**
 * 日付の0時設定
 * @param {Date} input
 * @returns "YYYY-MM-DD 00:00:00"
 */
export function dateToDatabase(input: Date) {
    const yyyy = input.getFullYear()
    const MM = ('0' + (input.getMonth() + 1)).slice(-2)
    const dd = ('0' + input.getDate()).slice(-2)
    return yyyy + '-' + MM + '-' + dd + ' 00:00:00'
}

/**
 *
 * @param {string} input 'YYYY/M/D H:MM:SS'
 * @param {boolean} hasSecond default true
 * @returns 'YYYY/MM/DD HH:MM:SS' or 'YYYY/MM/DD HH:MM'
 */
export function formatYMDHMS(input: string, hasSecond = true) {
    if (!input) return '-'
    const date = new Date(dateString(input))
    const YYYY = date.getFullYear()
    const MM = ('0' + (date.getMonth() + 1)).slice(-2)
    const dd = ('0' + date.getDate()).slice(-2)
    const hh = ('0' + date.getHours()).slice(-2)
    const mm = ('0' + date.getMinutes()).slice(-2)
    const ss = ('0' + date.getSeconds()).slice(-2)
    return hasSecond
        ? `${YYYY}-${MM}-${dd} ${hh}:${mm}:${ss}`
        : `${YYYY}-${MM}-${dd} ${hh}:${mm}`
}

/**
 * N時間前の日時
 * @param {number} n hours ago
 * @returns 'YYYY-MM-DDTHH:MM'
 */
export function hoursAgo(n: number) {
    const now = new Date()
    now.setHours(now.getHours() - n)
    const year = now.getFullYear()
    const month = ('00' + (now.getMonth() + 1)).slice(-2)
    const day = ('00' + now.getDate()).slice(-2)
    const hour = ('00' + now.getHours()).slice(-2)
    return `${year}-${month}-${day}T${hour}:00`
}

/**
 * ----------------------------- 年月日 -----------------------------
 */

/**
 * これinputの型がなぞ、要調査 mtsm-todo
 * @param input 'YYYY-MM-DDTHH:MM:SS.000000Z'
 * @returns 'YYYY-MM-DD'
 */
export function formatYMD(input: string) {
    if (!input) return '1977-01-01'
    const date = new Date(dateString(input))
    const year = date.getFullYear()
    const month = ('00' + (date.getMonth() + 1)).slice(-2)
    const day = ('00' + date.getDate()).slice(-2)
    return `${year}-${month}-${day}`
}

/**
 * 別形式の対応（船員登録時に使用）
 * @param {Date} input 'Sun Mar 31 2024 00:00:00 GMT+0900'
 * @returns 'YYYY-MM-DD'
 */
export function formatGMT2YMD(input: string) {
    if (!input) return '1977-01-01'
    const date = new Date(dateStringTZ(input))
    const year = date.getFullYear()
    const month = ('00' + (date.getMonth() + 1)).slice(-2)
    const day = ('00' + date.getDate()).slice(-2)
    return `${year}-${month}-${day}`
}

/**
 * Date OjbectをYYYY-MM-DDにフォーマット
 * @param {*} date 引数ない場合今日の日付
 * @returns YYYY-MM-DD
 */
export function dateToYYMMDD(date: Date) {
    const targetDate = date ? date : new Date()
    const YYYY = targetDate.getFullYear()
    const M = targetDate.getMonth() + 1
    const MM = ('00' + M).slice(-2)
    const D = targetDate.getDate()
    const DD = ('00' + D).slice(-2)
    return `${YYYY}-${MM}-${DD}`
}

/**
 * 船員名見出しの日付フォーマット
 * @param {string} date YYYY-MM-DD
 * @param {string} locale ja,en
 * @returns YYYY/MM/DD(d)
 */
export function displayDate(date: string, locale: string) {
    const dateString = date.toString().replace(/-/g, '/')
    return `${dateString}(${getWeekDay(date, locale).text})`
}

export function displayDateForInternationalDateList(
    date: WorkdaysType,
    locale: string
) {
    const dateString = date.label.toString().replace(/-/g, '/')
    const number =
        date.number === 1 ? '- 1st' : date.number === 2 ? '- 2nd' : ''
    return `${dateString}(${getWeekDay(date.label, locale).text}) ${number}`
}

/**
 * ----------------------------- 時分 -----------------------------
 */

/**
 * HH:MMの時間の差分をHH:MM形式で返す
 * @param time1 HH:MM
 * @param time2 HH:MM
 * @returns HH:MM
 */
export function diffHHMM(time1: string, time2: string) {
    const [hoursOfTime1, minutesOfTime1] = time1.split(':').map(Number)
    const totalMinutesOfTime1 = hoursOfTime1 * 60 + minutesOfTime1
    const [hoursOfTime2, minutesOfTime2] = time2.split(':').map(Number)
    const totalMinutesOfTime2 = hoursOfTime2 * 60 + minutesOfTime2
    const diff = totalMinutesOfTime1 - totalMinutesOfTime2
    if (diff >= 0) {
        return `${Math.floor(diff / 60)}:${('00' + (diff % 60)).slice(-2)}`
    } else if (Math.ceil(diff / 60) === 0) {
        return `-0:${('00' + (diff % 60)).slice(-2)}`
    } else {
        return `${Math.ceil(diff / 60)}:${('00' + (diff % 60)).slice(-2)}`
    }
}

/**
 * ----------------------------- 時分秒 -----------------------------
 */

/**
 * 日時文字列から日時
 * @param input たぶん"YYYY-MM-DD 00:00:00" 要確認
 * @returns 'HH:MM'
 */
export function formatHHMM(input: string) {
    if (!input) return '--:--'
    const inputReplace = input.toString().replace(/-/g, '/')
    const date = new Date(inputReplace)
    const hour = ('00' + date.getHours()).slice(-2)
    const minute = ('00' + date.getMinutes()).slice(-2)
    return `${hour}:${minute}`
}

/**
 * ミリ秒から時分秒
 * @param seconds
 * @returns
 */
export function seconds2HHMMSS(seconds: number) {
    const H = Math.floor(seconds / (60 * 60))
    const HH = H < 10 ? ('00' + H).slice(-2) : H
    const M = Math.floor(((seconds % (24 * 60 * 60)) % (60 * 60)) / 60)
    const MM = ('00' + M).slice(-2)
    const S = ((seconds % (24 * 60 * 60)) % (60 * 60)) % 60
    const SS = ('00' + S).slice(-2)
    return HH + ':' + MM + ':' + SS
}

/**
 * ミリ秒から時間
 * output "HH:MM" or "-"
 */
export function milliSecToHourMin(ms: number) {
    const min = Math.floor(ms / 1000 / 60) % 60
    const hour = Math.floor(ms / 1000 / 60 / 60)
    if (!min && !hour) return '-'
    return `${('0' + hour).slice(-2)}:${('0' + min).slice(-2)}`
}

/**
 * ミリ秒から時間(24時間超え可)
 * @param {number} ms
 * @returns "HHH:MM:SS"
 */
export function milliSecToHoursMMSS(ms: number) {
    if (ms <= 0) return '-'
    const n = (ms + 7000) / 1000
    const H = Math.floor(n / 3600)
    const HH = H < 10 ? ('00' + H).slice(-2) : H
    const M = Math.floor(((n % (24 * 60 * 60)) % (60 * 60)) / 60)
    const MM = ('00' + M).slice(-2)
    const S = ms / 1000 - H * 3600 - M * 60
    const SS = S <= 59 ? ('00' + S).slice(-2) : '00'
    return HH + ':' + MM + ':' + SS
}

/**
 * ミリ秒から時間(24時間超えはERROR)
 * @param {number} ms
 * @returns "HH:MM" or ERROR
 */
export function milliSecToHHMM(ms: number) {
    if (ms <= 0) {
        return '-'
    } else if (ms > 24 * 60 * 60 * 1000) {
        return 'ERROR'
    } else {
        const n = (ms + 1000) / 1000
        const H = Math.floor(n / 3600)
        const HH = ('00' + H).slice(-2)
        const M = Math.floor(((n % (24 * 60 * 60)) % (60 * 60)) / 60)
        const MM = ('00' + M).slice(-2)
        return HH + ':' + MM
    }
}

/**
 * ミリ秒から時間(24時間超えはERROR)
 * @param {number} ms
 * @returns "HH:MM:SS" or ERROR
 */
export function milliSecToHHMMSS(ms: number) {
    if (ms <= 0) {
        return '-'
    } else if (ms > 24 * 60 * 60 * 1000) {
        return 'ERROR'
    } else {
        const n = (ms + 1000) / 1000
        const H = Math.floor(n / 3600)
        const HH = ('00' + H).slice(-2)
        const M = Math.floor(((n % (24 * 60 * 60)) % (60 * 60)) / 60)
        const MM = ('00' + M).slice(-2)
        const S = ms / 1000 - H * 3600 - M * 60
        const SS = S <= 59 ? ('00' + S).slice(-2) : '00'
        return HH + ':' + MM + ':' + SS
    }
}

/**
 * ミリ秒から時間(24時間超えは24:00)
 * @param {number} ms
 * @returns "HH:MM" max 24:00, min 00:00
 */
export function milliSecToHHMM24(ms: number, isString = false) {
    if (ms === null) {
        return '-'
    } else if (ms <= 0) {
        return '00:00'
    } else if (ms > 24 * 60 * 60 * 1000) {
        return isString ? '24時間' : '24:00'
    } else {
        const n = (ms + 1000) / 1000
        const H = Math.floor(n / 3600)
        const HH = ('00' + H).slice(-2)
        const M = Math.floor(((n % (24 * 60 * 60)) % (60 * 60)) / 60)
        const MM = ('00' + M).slice(-2)
        const hString = i18n.t('ship.labor.record.hString')
        const mString = i18n.t('ship.labor.record.mString')
        return isString
            ? M === 0
                ? `${H}${hString}`
                : `${H}${hString}${M}${mString}`
            : HH + ':' + MM
    }
}

/**
 * ミリ秒から時間(24時間超えは24:00:00)
 * @param {number} ms
 * @returns "HH:MM:SS" max 24:00:00, min 00:00:00
 */
export function milliSecToHHMMSS24(ms: number) {
    if (ms === null) {
        return '-'
    } else if (ms <= 0) {
        return '00:00:00'
    } else if (ms > 24 * 60 * 60 * 1000) {
        return '24:00:00'
    } else {
        const n = (ms + 1000) / 1000
        const H = Math.floor(n / 3600)
        const HH = ('00' + H).slice(-2)
        const M = Math.floor(((n % (24 * 60 * 60)) % (60 * 60)) / 60)
        const MM = ('00' + M).slice(-2)
        const S = ms / 1000 - H * 3600 - M * 60
        const SS = S <= 59 ? ('00' + S).slice(-2) : '00'
        return HH + ':' + MM + ':' + SS
    }
}

/**
 * 継続時間
 * @param {string} start "2022-06-23 16:12:06"
 * @param {string} end "2022-06-23 16:12:06"
 * @param {boolean} hasSecond
 * @returns HH:MM:SS or HH:MM
 */
export function durationHHMMSS(start: string, end: string, hasSecond = true) {
    const startDate = new Date(dateString(start))
    const endDate = new Date(dateString(end))

    const diff = (endDate.getTime() - startDate.getTime()) / 1000
    const H = Math.floor(diff / (60 * 60))
    const HH = H < 10 ? ('00' + H).slice(-2) : H
    const M = Math.floor(((diff % (24 * 60 * 60)) % (60 * 60)) / 60)
    const MM = ('00' + M).slice(-2)
    const S = ((diff % (24 * 60 * 60)) % (60 * 60)) % 60
    const SS = ('00' + S).slice(-2)
    return hasSecond ? `${HH}:${MM}:${SS}` : `${HH}:${MM}`
}

/**
 * 継続時間のミリ秒数
 * @param {string} start "2022-06-23 16:12:06"
 * @param {string} end "2022-06-23 16:12:06"
 * @returns {number} seconds
 */
export function durationSeconds(start: string, end: string) {
    const startSeconds = new Date(dateString(start)).getTime()
    const endSeconds = end
        ? new Date(dateString(end)).getTime()
        : new Date().getTime()
    return endSeconds - startSeconds
}

/**
 * タイムラインドラッグ時表示のHH:MM
 * @param left 左位置
 * @param firstColWidth 1列目の幅
 * @param unitWidth 1時間の幅
 * @returns
 */
export function dragLeftToHHMM(
    left: number,
    firstColWidth: number,
    unitWidth: number
) {
    const hours = (left - firstColWidth) / unitWidth
    const oneMinute = 60
    const oneHour = oneMinute * 60
    const seconds = Math.round(hours * oneHour)
    const h = Math.floor(seconds / oneHour)
    const HH = ('00' + h).slice(-2)
    const m = Math.round((seconds % oneHour) / oneMinute)
    const MM = ('00' + m).slice(-2)
    return HH + ':' + MM
}
/**
 * ----------------------------- ミリ秒 -----------------------------
 */

/**
 * 秒数差分計算
 * @param {string} start YYYY-MM-DD HH:MM:SS
 * @param {string} end YYYY-MM-DD HH:MM:SS
 * @returns ミリ秒
 */
export function calculateDiff(start: string, end: string) {
    if (!start) return 0
    const startSeconds = new Date(dateString(start)).getTime()
    const endSeconds = end
        ? new Date(dateString(end)).getTime()
        : new Date().getTime()
    return endSeconds - startSeconds
}

/**
 * 労働時間から休息秒数の逆算
 * @param {number} workingSeconds ミリ秒
 * @returns ミリ秒
 */
export function restSeconds(workingSeconds: number) {
    const hours24 = 24 * 60 * 60 * 1000
    if (hours24 - workingSeconds <= 0) {
        return 0
    } else {
        return hours24 - workingSeconds
    }
}

/**
 * 現在時刻から00分、15分、30分、45分までの秒数算出
 * @returns ミリ秒
 */
export function secondsToReload() {
    const now = new Date()
    const nowSeconds = now.getTime()
    const nowD = now.getDate()
    const nowH = now.getHours()
    const nowM = now.getMinutes()
    if (nowM < 10) {
        now.setMinutes(15, 0)
    } else if (nowM < 25) {
        now.setMinutes(30, 0)
    } else if (nowM < 40) {
        now.setMinutes(45, 0)
    } else if (nowM < 55) {
        now.setHours(now.getHours() + 1, 0, 0)
    } else if (nowH !== 23) {
        now.setHours(now.getHours() + 1, 15, 0)
    } else {
        now.setDate(nowD + 1)
        now.setHours(0, 15, 0)
    }
    const targetSeconds = now.getTime()
    return targetSeconds - nowSeconds
}

/**
 * ----------------------------- 判定・そのた -----------------------------
 */

/**
 * 文字列の日時は未来かどうか
 * @param {string} input "YYYY-MM-DD HH:MM:SS"
 * @returns boolean
 */
export function isFuture(input: string) {
    const target = new Date(dateString(input)).getTime()
    const now = new Date().getTime()
    return target >= now
}

/**
 * 文字列の日時は今日かどうか
 * @param {string} input YYYY-MM-DD
 * @returns boolean
 */
export function isToday(input: string) {
    const today = new Date()
    const target = new Date(dateString(input))
    return (
        today.getFullYear() === target.getFullYear() &&
        today.getMonth() === target.getMonth() &&
        today.getDate() === target.getDate()
    )
}

/**
 * 文字列の日は今月かどうか
 * @param {string} string YYYY/MM/DD HH:MM:SS
 * @returns boolean
 */
export function isCurrentMonth(input: string) {
    const today = new Date()
    const target = new Date(dateString(input))
    return (
        today.getFullYear() === target.getFullYear() &&
        today.getMonth() === target.getMonth()
    )
}

/**
 * 打刻の開始時間終了時間が適切かどうか
 * @param {string} start
 * @param {string} end
 * @returns orderError: boolean, lessThanOneMinute: boolean
 */
export function isStampInOrder(start: string, end: string) {
    const startSeconds = new Date(dateString(start)).getTime()
    const endSeconds = new Date(dateString(end)).getTime()
    return {
        orderError: endSeconds < startSeconds,
        lessThanOneMinute: endSeconds - startSeconds < 60 * 1000,
    }
}

/**
 * 日付が変わったらcallback処理を行う
 * @param {*} callback
 */
export function checkDateChange(callback: () => void) {
    const currentDate = new Date()
    const timeUntilMidnight =
        new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate() + 1
        ).getTime() - currentDate.getTime()

    setTimeout(() => {
        console.warn('日付が変わりました')
        callback()
    }, timeUntilMidnight + 1000)
}

/**
 * 乗船日数を算出
 * @param start
 * @param end
 * @returns number
 */
export function onboardDuration(start: string, end: string) {
    const startReplace = start.toString().replace(/-/g, '/')

    const startDate = new Date(startReplace)
    let endDate
    if (end) {
        const endReplace = end.toString().replace(/-/g, '/')
        endDate = new Date(endReplace)
    } else {
        endDate = new Date()
    }
    const diff = endDate.getTime() - startDate.getTime()
    return Math.ceil(diff / (24 * 60 * 60 * 1000))
}

/**
 * 過去の日かどうか
 * @param date YYYY-MM-DD
 * @returns boolean
 */
export function isPastDay(date: string) {
    const target = new Date(dateString(date))
    target.setHours(0)
    target.setMinutes(0)
    target.setSeconds(0)
    const today = new Date()
    today.setHours(0)
    today.setMinutes(0)
    today.setSeconds(0)
    const diff = today.getTime() - target.getTime()
    return diff >= 1000 * 24 * 60 * 60
}

export function isFirstDayOfMonth(date: string) {
    const target = new Date(dateString(date))
    return target.getDate() === 1
}

/**
 * ○ヶ月以内かどうか
 * @param date YYYY-MM-DD
 * @returns boolean
 */
export function isWithinAPeriod(date: string, period: number) {
    const today = new Date()
    today.setMonth(today.getMonth() - period)

    return dateToYYMMDD(today) > formatYMD(date)
}
